import { toNumber } from '@/lib/decimal'
import { formatContractHours } from '@/lib/format'
import { type PlanningCell, STAGE_BANDS, type StageBand } from '@/lib/planning-types'
import { cn } from '@/lib/utils'

/**
 * Hoe zekerder het werk, hoe voller de band. De ladder loopt door dezelfde
 * accentkleur en voegt er geen tweede kleur aan toe: dicht is toegezegd, open is
 * verwacht, gearceerd staat nog nergens. Een eigen kleur per trap zou ze als
 * verschillende soorten werk laten lezen in plaats van als hetzelfde werk met
 * een andere kans.
 */
export const BAND_FILL: Record<StageBand, string> = {
  won: 'bg-accent-600',
  likely: 'bg-accent-600/70',
  possible: 'bg-accent-600/45 ring-1 ring-inset ring-accent-600/55',
  speculative: 'bg-accent-600/25 ring-1 ring-inset ring-accent-600/45',
  undistributed: 'fill-hatch ring-1 ring-inset ring-accent-600/35',
}

export const BAND_LABEL: Record<StageBand, string> = {
  won: 'Gewonnen',
  likely: 'Waarschijnlijk',
  possible: 'Mogelijk',
  speculative: 'Speculatief',
  undistributed: 'Onverdeeld',
}

/** Verwacht werk in het algemeen; nog in gebruik buiten de vooruitblik. */
const UNCERTAIN_FILL = 'bg-accent-600/20 ring-1 ring-inset ring-accent-600/45'

/**
 * Het streven als eigen grens: alleen zodra het onder de beschikbare uren ligt.
 * Iedereen heeft een streven en wie niets heeft afgesproken staat op honderd
 * procent, dus dan valt het samen met de capaciteit. Dat is geen tweede grens
 * maar dezelfde, twee keer verteld, en daar wordt geen beeld en geen zin beter
 * van.
 */
export function separateTarget(cell: PlanningCell): number | null {
  if (cell.targetHours === null || cell.capacityHours === null) return null

  const target = toNumber(cell.targetHours)

  return target > 0 && target < toNumber(cell.capacityHours) ? target : null
}

/**
 * Wat de balk laat zien, in woorden. De kleur mag nooit het enige signaal zijn:
 * wie hem niet ziet leest hier hoe iemand ervoor staat.
 */
export function capacitySummary(cell: PlanningCell): string {
  const planned = formatContractHours(toNumber(cell.plannedHours))
  if (cell.capacityHours === null) return `${planned} u gepland, beschikbare uren onbekend`

  const target = separateTarget(cell)
  const parts = [`${planned} u gepland van ${formatContractHours(toNumber(cell.capacityHours))} u beschikbaar`]
  if (target !== null) parts.push(`streven ${formatContractHours(target)} u`)

  if (cell.overbooked) parts.push('boven de beschikbare uren')
  else if (cell.overTarget) parts.push('boven het streven')
  else if (target !== null) parts.push('binnen het streven')

  return parts.join(', ')
}

/**
 * Een balk die de hele boodschap draagt: waar het werk vandaan komt, hoe zeker
 * het is, en waar de grenzen liggen.
 *
 * De balk schaalt op het grootste van werk en capaciteit, zodat overboeking
 * zichtbaar over de markering heen loopt in plaats van tegen 100% te klemmen.
 * Zonder capaciteitsbasis is er geen grens om tegen af te zetten; de balk toont
 * dan wel het werk maar zwijgt over vol of leeg, wat eerlijker is dan een volle
 * balk die suggereert dat iemand aan zijn grens zit.
 *
 * Er zijn twee grenzen en ze zijn niet even erg: voorbij je beschikbare uren is
 * werk beloofd dat er niet is, voorbij je streven zit je vol maar klopt het nog.
 * Zonder afgesproken percentage vallen ze samen en blijft er een over. Die
 * rangorde draagt de tekening op drie manieren, zonder een tweede kleurenfamilie
 * te introduceren.
 *
 * 1. Gewicht. De capaciteitsgrens is een streep van 2px, het streven een haartje
 *    van 1px. Ook zonder kleur is te zien welke grens de zwaarste is.
 * 2. Precies een alarm tegelijk. Tussen streven en capaciteit kleurt het haartje
 *    `status-approval` (de attentie-kleur van de app, niet nieuw); zodra je over
 *    de capaciteit gaat neemt `status-overdue` op de zware streep het over en
 *    valt het haartje terug op neutraal. Twee gekleurde markeringen die om
 *    dezelfde balk vechten bestaan dus niet: het streven is dan niet meer het
 *    verhaal.
 * 3. De banden houden hun dichtheid. Alleen de markeringen kleuren, zoals het
 *    designsysteem voorschrijft.
 *
 * De kleur volgt de balklengte: wat meetelt drukt op de maand, dus zodra het werk
 * over een grens loopt kleurt die grens ook.
 */
export function CapacityBar({
  cell,
  size = 'person',
  described = false,
}: {
  cell: PlanningCell
  size?: 'person' | 'team'
  /**
   * De knop eromheen draagt de samenvatting al als beschrijving. De balk zwijgt
   * hier dus in tekst: dezelfde zin binnen de knop zou in de naam belanden en dan
   * leest een schermlezer hem twee keer, een keer als naam en een keer als
   * beschrijving.
   */
  described?: boolean
}) {
  const capacity = cell.capacityHours === null ? null : toNumber(cell.capacityHours)
  const planned = toNumber(cell.plannedHours)
  const target = separateTarget(cell)

  const scale = Math.max(capacity ?? 0, planned, 1)
  const width = (hours: number) => `${(hours / scale) * 100}%`

  return (
    <span
      className={cn(
        'relative flex flex-1 overflow-hidden rounded-full',
        size === 'team' ? 'h-3' : 'h-2',
        capacity === null ? 'bg-transparent' : 'bg-fill-3',
      )}
    >
      {STAGE_BANDS.map((band) => {
        const hours = toNumber(cell.byStage[band])
        if (hours <= 0) return null

        return <span key={band} className={cn('h-full', BAND_FILL[band])} style={{ width: width(hours) }} />
      })}

      {/* Het streven. Staat er altijd zodra het onder de grens ligt, ook als er nog
          ruimte onder zit: het is een mikpunt om op te sturen, geen alarm. */}
      {target !== null ? (
        <span
          aria-hidden
          className={cn(
            'absolute inset-y-0 w-px',
            cell.overTarget && !cell.overbooked ? 'bg-status-approval' : 'bg-ink-faint/70',
          )}
          style={{ left: width(target) }}
        />
      ) : null}

      {/* De harde grens. Staat boven de banden, want juist waar het werk eroverheen
          loopt moet je kunnen zien hoever. Alleen zichtbaar als het werk er ook
          echt overheen gaat, en dan is de maand per definitie overbezet. */}
      {capacity !== null && capacity > 0 && planned > capacity ? (
        <span
          aria-hidden
          className="absolute inset-y-0 w-0.5 bg-status-overdue"
          style={{ left: `calc(${width(capacity)} - 1px)` }}
        />
      ) : null}

      {described ? null : <span className="sr-only">{capacitySummary(cell)}</span>}
    </span>
  )
}

export { UNCERTAIN_FILL }
